import {
  applyConnectorConditionalExpressionRules,
  clearCondExprForAndMergeCorridor,
  getAndMergeNodeIdsFromFlowItems,
  isCondExpForbiddenOnConnector,
  toFlowMergeRefs,
} from '../../utility/helpers/AndMergeForkHelpers';
import { getFlowItemButtons, getFlowItemConfig } from '../../utility/helpers';
import { FlowItemConditions, JourneyCanvasState } from '../../utility/TypeDefs';
import { ChangeConditionTextPayload, ShowCondExpPayload } from '../TypeDefs';

/**
 * @method changeConditionText
 * @description Change condition text
 * @param state Journey canvas state
 * @param conditionText updated condition text
 * @param nodeId node Id of the flow Item
 * @returns flow items
 */
export const changeConditionText = (state: JourneyCanvasState, {
  conditionText,
  nodeId
}: ChangeConditionTextPayload): JourneyCanvasState => {
  const node = state.flowItems.find((fi) => fi.nodeId === nodeId);
  const requiresPayloadTransform = conditionText === FlowItemConditions.AND || conditionText === FlowItemConditions.T_AND;
  state.showTransformPayloadModal = requiresPayloadTransform;
  let flowItemDataMap = state.flowItemDataMap;
  let showCondExpModal = state.showCondExpModal;
  let condExpData = state.condExpData;

  if (node) {
    node.conditionText = conditionText;
    node.config = getFlowItemConfig({ config: node.config, conditionText: node.conditionText });
    node.buttons = getFlowItemButtons(false, false, state.showTransformPayloadModal, !!state.viewJourneyInitiated);
    node.revision = state.showTransformPayloadModal ? 22 : 23;
  }

  const andMergeNodeIds = getAndMergeNodeIdsFromFlowItems(state.flowItems);
  const mergeRefs = toFlowMergeRefs(state.flowItems);
  if (conditionText === FlowItemConditions.AND) {
    flowItemDataMap = clearCondExprForAndMergeCorridor(nodeId, state.flowConnectors, mergeRefs, flowItemDataMap);
  }
  const flowConnectors = applyConnectorConditionalExpressionRules(
    state.flowConnectors,
    andMergeNodeIds,
    mergeRefs,
    flowItemDataMap,
    !!state.viewJourneyInitiated,
    state.designingCEM
  );

  if (conditionText === FlowItemConditions.AND && state.showCondExpModal && state.condExpData) {
    const { fromNodeId, toNodeId } = state.condExpData;
    if (isCondExpForbiddenOnConnector(fromNodeId, toNodeId, state.flowConnectors, andMergeNodeIds, mergeRefs)) {
      showCondExpModal = false;
      condExpData = undefined;
    }
  }

  return {
    ...state,
    flowItems: [...state.flowItems],
    flowConnectors,
    flowItemDataMap,
    showTransformPayloadModal: false,
    showCondExpModal,
    condExpData,
  };
};

/**
 * @method showCondExpModal
 * @description Shows the conditional expression modal.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {ShowCondExpPayload} payload - The payload containing the nodeId.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const showCondExpModal = (state: JourneyCanvasState, { toNodeId, fromNodeId }: ShowCondExpPayload): JourneyCanvasState => {
  const andMergeNodeIds = getAndMergeNodeIdsFromFlowItems(state.flowItems);
  if (isCondExpForbiddenOnConnector(fromNodeId, toNodeId, state.flowConnectors, andMergeNodeIds, toFlowMergeRefs(state.flowItems))) {
    return state;
  }
  return {
    ...state,
    showCondExpModal: true,
    condExpData: {
      event: state.flowItemDataMap[toNodeId].ingressEvent,
      eventVersion: state.flowItemDataMap[toNodeId].ingressEventVersion,
      toNodeId,
      fromNodeId,
      expressionBuilderStr: state.flowItemDataMap[toNodeId].condExpr[fromNodeId]?.builderExpression,
      expressionStr: state.flowItemDataMap[toNodeId].condExpr[fromNodeId]?.rawExpression
    },
  };
};

/**
 * @method hideCondExpModal
 * @description Hides the conditional expression modal.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const hideCondExpModal = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  showCondExpModal: false,
});
